<template>
  <div>
    <h3 class="mb-3">Изменить отзыв</h3>
    <form @submit.prevent="updateHandler">
      <div class="mb-3">
        <h2 class="required mb-2">Флорист</h2>
        <va-select
          v-model="$v.form.florist_id.$model"
          :options="florists"
          text-by="name"
          value-by="id"
          placeholder="Выберите флориста"
        />
        <div
          v-if="!$v.form.florist_id.required && $v.form.florist_id.$error"
          class="error-message"
        >
          Обязательно для заполнения
        </div>
      </div>
      <div class="mb-3">
        <h2 class="required mb-2">Имя ревьюера</h2>
        <va-input
          v-model.trim="$v.form.reviewer_name.$model"
          class="mb-0"
          placeholder="Имя ревьюера"
        />
        <div
          v-if="!$v.form.reviewer_name.required && $v.form.reviewer_name.$error"
          class="error-message"
        >
          Обязательно для заполнения
        </div>
      </div>
      <div class="mb-3">
        <h2 class="required mb-2">Дата</h2>
        <va-input
          v-model="$v.form.date.$model"
          type="date"
          class="mb-0"
        />
        <div
          v-if="!$v.form.date.required && $v.form.date.$error"
          class="error-message"
        >
          Обязательно для заполнения
        </div>
      </div>
      <div class="mb-3">
        <h2 class="required mb-2">Рейтинг</h2>
        <va-input
          v-model.number="$v.form.rating.$model"
          type="number"
          min="0"
          max="5"
          step="0.1"
          class="mb-0"
          placeholder="От 0 до 5"
        />
        <div
          v-if="!$v.form.rating.required && $v.form.rating.$error"
          class="error-message"
        >
          Обязательно для заполнения
        </div>
      </div>
      <div class="mb-3">
        <h2 class="mb-2">Текст отзыва</h2>
        <va-input
          v-model.trim="form.text"
          type="textarea"
          class="mb-0"
          :min-rows="4"
          :max-rows="8"
          placeholder="Текст отзыва"
        />
      </div>
      <div class="mb-3">
        <label for="file-upload" class="custom-file-upload">
          Загрузить фото отзыва
        </label>
        <input
          id="file-upload"
          ref="fileInput"
          type="file"
          multiple
          class="file-input"
          accept="image/x-png,image/jpg,image/jpeg"
          @input="onFilePicked($event)"
        />
      </div>
      <div class="mb-3">
        <div v-if="existingPhotos.length" class="mb-2">
          <h4>Текущие фото:</h4>
          <div class="photos-row">
            <div
              v-for="(photo, index) in existingPhotos"
              :key="photo.id"
              class="photo"
            >
              <img class="photo-img" :src="photo.url" alt="review-photo" />
              <va-button
                color="danger"
                small
                @click.prevent="removeExistingPhoto(index)"
              >
                Удалить
              </va-button>
            </div>
          </div>
        </div>
        <div v-if="imageData.length" class="mb-2">
          <h4>Новые фото:</h4>
          <div class="photos-row">
            <div
              v-for="(img, index) in imageData"
              :key="index"
              class="photo"
            >
              <img class="photo-img" :src="img.url" alt="review-photo" />
              <va-button
                color="danger"
                small
                @click.prevent="removeImg(index)"
              >
                Удалить
              </va-button>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-3">
        <va-button color="danger" class="mr-1" @click.prevent="$emit('close')">
          Отменить
        </va-button>
        <va-button color="info" type="submit"> Сохранить </va-button>
      </div>
    </form>
  </div>
</template>

<script>
import api from '@/api/axios';
import { mapMutations } from 'vuex';
import { required } from 'vuelidate/lib/validators';

function getOmskToday() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Omsk',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  return year && month && day ? `${year}-${month}-${day}` : '';
}

export default {
  name: 'FloristReviewUpdate',
  props: {
    review: {
      type: Object,
      default: () => ({}),
    },
    florists: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      form: {
        florist_id: this.review.florist_id || null,
        reviewer_name: this.review.reviewer_name || '',
        date: this.review.date ? this.review.date.split('T')[0] : getOmskToday(),
        text: this.review.text || '',
        rating: this.review.rating || 5,
      },
      existingPhotos: [...(this.review.photos || [])],
      selectedFiles: [],
      imageData: [],
      imgUploadLoader: false,
    };
  },
  validations: {
    form: {
      florist_id: {
        required,
      },
      reviewer_name: {
        required,
      },
      date: {
        required,
      },
      rating: {
        required,
      },
    },
  },
  methods: {
    ...mapMutations(['SET_GLOBAL_LOADER']),
    async updateHandler() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return false;
      } else {
        this.SET_GLOBAL_LOADER(true);
        try {
          let photoIds = this.existingPhotos.map((p) => p.id);
          
          if (this.selectedFiles.length > 0) {
            const formData = new FormData();
            for (let i = 0; i < this.selectedFiles.length; i++) {
              formData.append(`file[${i}]`, this.selectedFiles[i], this.selectedFiles[i].name);
            }
            const res = await api.post('/upload-file', formData);
            const newPhotoIds = res.data.map((item) => item.id);
            photoIds = [...photoIds, ...newPhotoIds];
          }

          const data = {
            florist_id: this.form.florist_id,
            reviewer_name: this.form.reviewer_name,
            date: this.form.date,
            text: this.form.text || null,
            rating: this.form.rating,
            photos: photoIds,
          };

          await api.put(`/admin/florist-reviews/${this.review.id}`, data);
          this.$emit('updated');
          this.SET_GLOBAL_LOADER(false);
        } catch (error) {
          console.error('Ошибка обновления отзыва:', error);
          this.SET_GLOBAL_LOADER(false);
          alert('Ошибка при обновлении отзыва');
        }
      }
    },
    onFilePicked(event) {
      const files = Array.from(event.target.files);
      files.forEach((file) => {
        if (this.imageData.length + this.existingPhotos.length >= 10) {
          alert('Максимум 10 фото');
          return;
        }
        this.selectedFiles.push(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imageData.push({ url: e.target.result });
        };
        reader.readAsDataURL(file);
      });
    },
    removeImg(index) {
      this.imageData.splice(index, 1);
      this.selectedFiles.splice(index, 1);
    },
    removeExistingPhoto(index) {
      this.existingPhotos.splice(index, 1);
    },
  },
};
</script>

<style>
.photos-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.photo {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.photo-img {
  width: 150px;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
}
</style>

